/**
 * Team feedback on an applicant (flag applicant_view_v2): internal notes plus one 1 to 5 rating per
 * team member. The candidate never sees any of it.
 *
 * Data (client-written, enforced by firestore.rules "TEAM FEEDBACK"):
 *   applications/{applicationId}/teamFeedback/{authorUid}
 *       { authorUid, authorName, rating (int 1..5), applicationId, jobId, emtId, orgId, createdAt, updatedAt }
 *       One per team member (the doc id IS the author's uid), editable only by its author.
 *       orgId / jobId / emtId are copied from the application and its job and checked by the rules,
 *       so the org's ratings can be read with one collection-group query for the list "Rating" column.
 *   applications/{applicationId}/teamNotes/{noteId}
 *       { authorUid, authorName, text (1..NOTE_MAX_LENGTH), createdAt }
 *       Append-only: a note is never edited or deleted from the app.
 * Who: employers of the job's org (the posting employer, or an employer whose orgId is the job's),
 * the same membership rule the applications update rule uses. The candidate, other orgs and
 * signed-out visitors are refused, and nobody can write a rating or note as someone else.
 *
 * Pure: no Firebase.
 */
import { toMs } from './applicantHistory';

export const APPLICANT_VIEW_FLAG = 'applicant_view_v2';
export const APPLICANT_VIEW_FLAG_LABEL = 'Applicant view with tabs and team feedback';
export const APPLICANT_VIEW_FLAG_DESCRIPTION =
  'The employer applicant page gets tabs (Summary, Resume, Answers, Credentials, Feedback, History), a "Not moving forward" action, private team notes and 1 to 5 ratings (average shown on applicant lists), and a Source column for where each application came from. Off: the existing applicant page and lists.';

export const RATING_MIN = 1;
export const RATING_MAX = 5;
export const NOTE_MAX_LENGTH = 2000;
export const AUTHOR_NAME_MAX_LENGTH = 120;

export const TEAM_FEEDBACK_COLLECTION = 'teamFeedback';
export const TEAM_NOTES_COLLECTION = 'teamNotes';

export function isValidRating(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= RATING_MIN && value <= RATING_MAX;
}

/** A note as stored: whitespace tidied, capped. Null when there is nothing to save. */
export function cleanNoteText(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.replace(/\r\n/g, '\n').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  return t ? t.slice(0, NOTE_MAX_LENGTH) : null;
}

export function cleanAuthorName(raw: unknown): string {
  const t = typeof raw === 'string' ? raw.replace(/\s+/g, ' ').trim() : '';
  return (t || 'Team member').slice(0, AUTHOR_NAME_MAX_LENGTH);
}

export type RatingLike = { rating?: unknown; authorUid?: unknown };

export type RatingSummary = {
  /** Average of the valid ratings, rounded to one decimal; null when nobody has rated. */
  average: number | null;
  count: number;
};

/** Average of the valid 1..5 ratings (one per author: a repeated author counts once, the last wins). */
export function summarizeRatings(rows: ReadonlyArray<RatingLike>): RatingSummary {
  const byAuthor = new Map<string, number>();
  let anon = 0;
  for (const r of rows) {
    if (!isValidRating(r.rating)) continue;
    if (typeof r.authorUid === 'string' && r.authorUid) byAuthor.set(r.authorUid, r.rating);
    else byAuthor.set(`#${anon++}`, r.rating);
  }
  const values = [...byAuthor.values()];
  if (values.length === 0) return { average: null, count: 0 };
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  return { average: Math.round(avg * 10) / 10, count: values.length };
}

/** "4.5 of 5 (2 ratings)" / "Not rated yet". Words, never stars alone. */
export function formatRatingSummary(s: RatingSummary): string {
  if (s.average === null || s.count === 0) return 'Not rated yet';
  const avg = Number.isInteger(s.average) ? String(s.average) : s.average.toFixed(1);
  return `${avg} of 5 (${s.count} rating${s.count === 1 ? '' : 's'})`;
}

/** Compact form for list rows: "4.5 (2)" or "None". */
export function formatRatingShort(s: RatingSummary): string {
  if (s.average === null || s.count === 0) return 'None';
  const avg = Number.isInteger(s.average) ? String(s.average) : s.average.toFixed(1);
  return `${avg} (${s.count})`;
}

/** Group org-wide rating rows by application id and summarize each. */
export function ratingSummariesByApplication(rows: ReadonlyArray<RatingLike & { applicationId?: unknown }>): Map<string, RatingSummary> {
  const grouped = new Map<string, RatingLike[]>();
  for (const r of rows) {
    if (typeof r.applicationId !== 'string' || !r.applicationId) continue;
    const list = grouped.get(r.applicationId) ?? [];
    list.push(r);
    grouped.set(r.applicationId, list);
  }
  const out = new Map<string, RatingSummary>();
  for (const [id, list] of grouped) out.set(id, summarizeRatings(list));
  return out;
}

export const RATING_WORDS: Record<number, string> = {
  1: 'Poor fit',
  2: 'Below expectations',
  3: 'Meets expectations',
  4: 'Strong',
  5: 'Excellent',
};

/** One rating as read back (the doc id is the author's uid). */
export type RatingRow = { authorUid: string; authorName: string; rating: number; applicationId: string; updatedAtMs: number };

export function toRatingRow(docId: string, data: Record<string, unknown> | null | undefined): RatingRow | null {
  if (!data || !isValidRating(data.rating)) return null;
  const authorUid = typeof data.authorUid === 'string' && data.authorUid ? data.authorUid : docId;
  return {
    authorUid,
    authorName: cleanAuthorName(data.authorName),
    rating: data.rating,
    applicationId: typeof data.applicationId === 'string' ? data.applicationId : '',
    updatedAtMs: toMs(data.updatedAt),
  };
}
